import React, { useMemo } from "react";
import styled from "styled-components";
import Clock from "@material-ui/icons/AccessTime";
import moment from "moment";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import {
  Meridiem,
  TimePickerList,
} from "@sonder/ui/source/styled/components/TimePickerList";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { borders } from "@sonder/ui/source/styled/theme";

const TimePickerListWithBorders = styled(TimePickerList)`
  border-right: ${borders.standard};
  + :last-child {
    border-right: none;
  }
`;

// Generate items to populate the hour and minute lists
const generateListItems = (count: number, step: number, startAt: number) =>
  [...Array(count)].map((_, i) => (i + startAt) * step);

// Given an hour in either 24 or 12hr format, and a meridiem value, returns a
// normalized hour value in 24hr, zero-indexed format (0-23)
const getNormalizedHour = (hour: number, meridiem: Meridiem) => {
  let nextHour = hour;
  // Convert from 24hr to 12hr format
  if (hour > 12) nextHour -= 12;
  // Hours are zero-indexed (0-11). Convert the 12th hour to 0
  if (hour === 12) nextHour -= 12;
  // Convert back to 24hr format, using the meridien value
  if (meridiem === "PM") nextHour += 12;
  return nextHour;
};

export interface RenderTriggerProps {
  date?: moment.Moment;
  disabled?: boolean;
}

interface TimePickerProps extends RenderTriggerProps {
  onChange?(nextDate: moment.Moment): void;
  onClose?(): void;
  onOpen?(): void;
  renderTrigger?: (args: RenderTriggerProps) => JSX.Element;
}

export const TimePickerTrigger = ({ date, disabled }: RenderTriggerProps) => (
  <Button iconLeft disabled={disabled}>
    <Clock />
    {date ? date.format("h:mm A") : "Set Time"}
  </Button>
);

export const TimePicker = ({
  date,
  disabled,
  onChange = () => undefined,
  onClose = () => undefined,
  onOpen = () => undefined,
  renderTrigger = TimePickerTrigger,
}: TimePickerProps) => {
  const backupDate = date || moment().minutes(0).seconds(0);
  // Parse hour, minute, and meridiem from the current date object
  const { hour, minute, meridiem } = useMemo(
    () => ({
      hour: backupDate.hour(),
      minute: backupDate.minute(),
      meridiem: moment()
        .localeData()
        .meridiem(backupDate.hour(), backupDate.minute(), false) as Meridiem,
    }),
    [backupDate],
  );

  return (
    <Dropdown
      trigger={renderTrigger({ date, disabled })}
      onClose={onClose}
      onOpen={onOpen}
    >
      {({ isOpen }) =>
        isOpen && (
          <DropdownContent>
            <TimePickerListWithBorders
              items={generateListItems(12, 1, 1)}
              selected={meridiem === "PM" ? hour - 12 : hour}
              onSelect={nextHour => {
                if (typeof nextHour !== "number") return;
                if (hour !== nextHour) {
                  onChange(
                    backupDate
                      .clone()
                      .hour(getNormalizedHour(nextHour, meridiem)),
                  );
                }
              }}
            />
            <TimePickerListWithBorders
              items={generateListItems(4, 15, 0)}
              selected={minute}
              onSelect={nextMinute => {
                if (typeof nextMinute !== "number") return;
                if (minute !== nextMinute) {
                  onChange(backupDate.clone().minute(nextMinute));
                }
              }}
            />
            <TimePickerListWithBorders
              items={["AM", "PM"]}
              selected={meridiem}
              onSelect={nextMeridiem => {
                if (typeof nextMeridiem !== "string") return;
                if (meridiem !== nextMeridiem) {
                  onChange(
                    backupDate
                      .clone()
                      .hour(getNormalizedHour(hour, nextMeridiem)),
                  );
                }
              }}
            />
          </DropdownContent>
        )
      }
    </Dropdown>
  );
};
